import { MenuItems as HeadlessMenuItems } from '@headlessui/react';
import clsx from 'clsx';

import React, {
  createContext,
  forwardRef,
  useState,
  useEffect,
  type ReactNode,
} from 'react';

import { createPortal } from 'react-dom';
import breakpoints from '../../design-tokens/tier-1-definitions/breakpoints';
import { assertEdsUsage } from '../../util/logging';
import type {
  NavButton,
  NavGroup,
  NavItem,
  NavLink,
  NavMenuButton,
  NavMenuLink,
  UserData,
} from '../../util/utility-types';

import Avatar from '../Avatar';
import Button from '../Button';
import Hr from '../Hr';
import Icon, { type IconName } from '../Icon';
import Menu from '../Menu';
import PopoverContainer from '../PopoverContainer';
import Text from '../Text';

import styles from './AppHeader.module.css';

export type AppHeaderEventHandler = (
  event: React.SyntheticEvent,
  navItem: NavItem,
) => void;

export type AppHeaderProps = {
  // Component API
  /**
   * CSS class names that can be appended to the component.
   */
  className?: string;
  /**
   * Handle the click event for a given clickable button nav item in the header. Includes the data from the associated/clicked `NavItem` for reference
   * (e.g., attaching events, tracking, etc.)
   */
  onButtonClick?: AppHeaderEventHandler;
  /**
   * Handle the click event for a given clickable link nav item in the header. Includes the data from the associated/clicked `NavItem` for reference
   * (e.g., attaching events, tracking, etc.)
   */
  onLinkClick?: AppHeaderEventHandler;
  // Design API
  /**
   * Web location for the home page. Use this to direct where the main page of the application lives.
   */
  href?: string;
  /**
   * Sets of navigation groups in the header. Consider using 2-3 at maximum. Each NavGroup can contain many NavItems
   */
  navGroups?: NavGroup[];
  /**
   * Sets the default orientation of the App Header.
   *
   * * `vertical` alignment places the navigation along the left edge of the screen
   * * `horizontal` alignment spans the top portion of the screen and can flex into a responsive display with an expanding vertical menu
   *
   * **Default is `"horizontal"`**.
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * Determines how the appheader attaches to the window.
   *
   * * `floating` describes an overlay appearance where `AppHeader` is not flush with the edge of the window
   * * `docked' describes an appearance where `AppHeader` is flush with the edge of the window
   */
  style?: 'floating' | 'docked';
  /**
   * Text used to describe the contents of the page with more detail.
   */
  subTitle?: string;
  /**
   * Element used for the application's logo (can be text or an image)
   */
  title: ReactNode;
};

type AppHeaderTitleProps = {
  // Component API
  // Design API
  // Insert props/values as defined in figma for AppHeader
  /**
   * Element used for the application's logo (can be text or an image)
   */
  title?: ReactNode;
  /**
   * Text used to describe the contents of the page with more detail.
   */
  subTitle?: string;
};

type AppHeaderNavGroupProps = NavGroup & {
  // Component API
  /**
   * CSS class names that can be appended to the component.
   */
  className?: string;
  /**
   * Handle the click event for any given button in the header
   */
  onButtonClick?: AppHeaderEventHandler;
  /**
   * Handle the click event for any given link in the header
   */
  onLinkClick?: AppHeaderEventHandler;
  // Design API
};

type AppHeaderLinkProps = NavLink &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    // Component API
    /**
     * CSS class names that can be appended to the component.
     */
    className?: string;
    /**
     * Whether it is appearing in a vertical orientation (like in a drawer)
     */
    isVertical?: boolean;
    // Design API
    /**
     * Marks whether the link will direct to an external resource or not
     */
    isExternal?: boolean;
  };

type AppHeaderButtonProps = NavButton &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    // Component API
    /**
     * CSS class names that can be appended to the component.
     */
    className?: string;
    /**
     * Handle the click event for any given button in the header
     */
    onButtonClick?: AppHeaderEventHandler;
    // Design API
    /**
     * Whether it is appearing in a vertical orientation (like in a drawer)
     */
    isVertical?: boolean;
    /**
     * Mark whether this `AppHeaderButton` is the current button, and apply the proper styling.
     */
    isCurrent?: boolean;
    /**
     * Content to use in the leading position of an app header button (allows icons or an `"avatar"`).
     */
    leadingContent?: IconName | 'avatar';
    /**
     * Data associated with the avatar. Only useful with `leadingContent` set to `"avatar"`
     */
    user?: UserData;
    /**
     * Content to use in the trailing position of an app header button.
     */
    trailingContent?: IconName;
  };

type AppHeaderDrawerProps = {
  /**
   * Determines the mode of the opening behavior of nav menus
   */
  mode?: 'drawer' | 'default';
  /**
   * Sets of navigation groups in the header. Consider using 2-3 at maximum. Each NavGroup can contain many NavItems
   */
  navGroups?: NavGroup[];
  /**
   * Handle the click event for any given button in the header
   */
  onButtonClick?: AppHeaderEventHandler;
  /**
   * Handle the click event for any given link in the header
   */
  onLinkClick?: AppHeaderEventHandler;
};

// Handling for any properties that should be used in sub-components
const AppHeaderContext = createContext<
  Pick<AppHeaderProps, 'orientation' | 'href'>
>({
  orientation: 'horizontal',
  href: '#',
});

function NavMenuItemLeadingContent(props: {
  navItem: NavMenuButton | NavMenuLink;
}): ReactNode {
  const { navItem } = props;
  let leadingContent: ReactNode = null;
  if (navItem.leadingContent === 'avatar') {
    leadingContent = <Avatar size="sm" user={navItem.user} />;
  } else if (navItem.leadingContent) {
    leadingContent = (
      <Icon name={navItem.leadingContent} purpose="decorative" size="24px" />
    );
  }

  return leadingContent;
}

function NavMenuItemTrailingContent(props: {
  navItem: NavMenuButton | NavMenuLink;
}): ReactNode {
  const { navItem } = props;
  let trailingContent: ReactNode = null;
  if (navItem.trailingContent) {
    trailingContent = (
      <Icon name={navItem.trailingContent} purpose="decorative" size="24px" />
    );
  }

  return trailingContent;
}

/**
 * Handler for whether a click on the associated NavItem should close the `Menu`
 * @param navItem NavItem associated with the nav menu (NavMenuLink or NavMenuButton)
 * @param close function provided to trigger close on a clicked menu item
 */
function handleShouldClose(
  ev: React.MouseEvent,
  navItem: NavMenuButton | NavMenuLink,
  close: () => void,
) {
  if (typeof navItem.shouldClose !== 'undefined') {
    navItem.shouldClose ? close() : ev.preventDefault();
  }
}

/**
 * `import {AppHeader} from "@chanzuckerberg/eds";`
 *
 * Provides app-level navigation and serves as the `<header>` element for accessibility landmarks
 */
export const AppHeader = ({
  className,
  href,
  navGroups,
  onButtonClick,
  onLinkClick,
  orientation,
  style = 'docked',
  subTitle,
  title,
  ...other
}: AppHeaderProps) => {
  const [headerOrientation, setHeaderOrientation] = useState(
    orientation || 'horizontal',
  );

  const componentClassName = clsx(
    styles['app-header'],
    headerOrientation && styles[`app-header--orientation-${headerOrientation}`],
    style && styles[`app-header--style-${style}`],
    className,
  );

  const drawerComponentClassName = clsx(
    style && styles[`app-header--style-${style}`],
    styles['app-header__drawer'],
  );

  const handleOrientationCalculation = function (
    orientation: AppHeaderProps['orientation'],
  ) {
    // compare the screen width to the smallest breakpoint. if it's wider...
    if (window.innerWidth > parseInt(breakpoints['eds-bp-sm'], 10)) {
      // ...change to original user value (with default specified)
      setHeaderOrientation(orientation || 'horizontal');
    } else {
      // ...change 'vertical' to 'horizontal' if the original value was vertical
      setHeaderOrientation('horizontal');
    }
  };

  useEffect(() => {
    const handler = () => {
      handleOrientationCalculation(orientation);
    };

    // Call it manually when this effect is triggered
    handleOrientationCalculation(orientation);

    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  }, [orientation]);

  return (
    <AppHeaderContext.Provider value={{ href, orientation: headerOrientation }}>
      <header className={componentClassName} {...other}>
        <div>
          <div className={styles['app-header__content']}>
            <div className={styles['app-header-title']}>
              {href ? (
                <a
                  aria-label="homepage"
                  className={styles['app-header__home-link']}
                  href={href}
                  onClick={(ev) => {
                    onLinkClick &&
                      onLinkClick(ev, {
                        name: 'EDS-header-logo',
                        type: 'link',
                        href: href,
                      } as NavLink);
                  }}
                >
                  <AppHeaderTitle subTitle={subTitle} title={title} />
                </a>
              ) : (
                <AppHeaderTitle subTitle={subTitle} title={title} />
              )}
            </div>
            {headerOrientation === 'horizontal' && (
              <>
                <div className={styles['app-header__nav-groups']}>
                  {navGroups?.map((navGroup) => (
                    <AppHeaderNavGroup
                      key={`navGroup-${navGroup.name}`}
                      name={navGroup.name}
                      navItems={navGroup.navItems}
                      onButtonClick={onButtonClick}
                      onLinkClick={onLinkClick}
                    />
                  ))}
                </div>
                {navGroups?.length ? (
                  <div className={styles['app-header__menu']}>
                    <AppHeaderButton
                      aria-label="Show Menu"
                      icon="menu"
                      iconLayout="icon-only"
                      name="hamburger-menu"
                      onClick={() => {
                        document.getElementById('popover')?.showPopover();
                      }}
                      type="button"
                    />
                  </div>
                ) : null}
              </>
            )}
            {headerOrientation === 'vertical' && (
              <AppHeaderDrawerContent
                navGroups={navGroups}
                onButtonClick={onButtonClick}
                onLinkClick={onLinkClick}
              />
            )}
          </div>
        </div>
      </header>
      {headerOrientation === 'horizontal' && navGroups?.length
        ? createPortal(
            <div
              className={drawerComponentClassName}
              id={'popover'}
              // @ts-expect-error popover properly supported in React 19
              popover="auto"
            >
              <div className={styles['app-header__drawer-button']}>
                <Button
                  aria-label="Close popover menu"
                  className={styles['app-header__drawer-button-instance']}
                  icon="close"
                  iconLayout="icon-only"
                  onClick={() => {
                    document.getElementById('popover')?.hidePopover();
                  }}
                  rank="tertiary"
                  size="lg"
                />
              </div>
              <AppHeaderDrawerContent
                mode="drawer"
                navGroups={navGroups}
                onButtonClick={onButtonClick}
                onLinkClick={onLinkClick}
              />
            </div>,
            document.body,
          )
        : null}
    </AppHeaderContext.Provider>
  );
};

/**
 * Sub-component for handling the title elements of the AppHeader
 *
 * @param props Properties for use with the AppHeaderTitleProps
 * @returns ReactNode
 */
const AppHeaderTitle = ({ title, subTitle }: AppHeaderTitleProps) => {
  const componentClassName = clsx(
    styles['app-header-title__title'],
    typeof title === 'object' && styles['app-header-title--has-logo'],
  );
  return (
    <>
      <div className={componentClassName}>
        {typeof title === 'string' ? (
          <Text as="span" preset="headline-sm">
            {title}
          </Text>
        ) : (
          <>{title}</>
        )}
      </div>
      {subTitle && (
        <div className={styles['app-header-title__sub-title']}>
          <Text as="span" preset="body-md">
            {subTitle}
          </Text>
        </div>
      )}
    </>
  );
};

/**
 * Sub-component for the individual navigation groups within the app header.
 *
 * @param props properties for AppHeaderNavGroupProps
 * @returns ReactNode
 */
const AppHeaderNavGroup = ({
  name,
  navItems,
  onButtonClick,
  onLinkClick,
  ...other
}: AppHeaderNavGroupProps) => {
  const componentClassName = clsx(styles['app-header__nav-group']);
  return (
    <nav aria-label={name} className={componentClassName} {...other}>
      <ul>
        {navItems.map((navItem) => {
          if (navItem.type === 'separator') return null;
          return (
            <li key={navItem.name}>
              {navItem.type === 'button' && (
                <AppHeaderButton
                  {...navItem}
                  onClick={(ev) => {
                    onButtonClick && onButtonClick(ev, navItem);
                  }}
                />
              )}
              {navItem.type === 'link' && (
                <AppHeaderLink
                  key={navItem.name}
                  {...navItem}
                  onClick={(ev) => {
                    onLinkClick && onLinkClick(ev, navItem);
                  }}
                />
              )}
              {(navItem.type === 'menu' || navItem.type === 'tree') && (
                <Menu>
                  {({ close, open }) => (
                    <>
                      {/* Consolidate handling of <Menu> into one function with each type handled separately */}
                      <Menu.PlainButton as={React.Fragment}>
                        <AppHeaderButton
                          icon={
                            navItem.type === 'tree' ? 'chevron-down' : undefined
                          }
                          iconLayout={
                            navItem.type === 'menu' &&
                            navItem.leadingContent === 'avatar'
                              ? 'left'
                              : navItem.iconLayout
                          }
                          isCurrent={open}
                          leadingContent={
                            navItem.type === 'menu'
                              ? navItem.leadingContent
                              : undefined
                          }
                          name={navItem.name}
                          trailingContent={
                            navItem.type === 'menu' ? 'chevron-down' : undefined
                          }
                          type="button"
                          user={
                            navItem.type === 'menu' ? navItem.user : undefined
                          }
                        >
                          {navItem.name}
                          {navItem.type === 'menu' && navItem.subLabel && (
                            <Text as="div" preset="appHeader-subLabel">
                              {navItem.subLabel}
                            </Text>
                          )}
                        </AppHeaderButton>
                      </Menu.PlainButton>
                      <Menu.Items
                        anchor={{ to: 'bottom end', gap: 12 }}
                        className={styles['app-header__nav-items']}
                      >
                        {navItem.navItems?.map((navItem) => {
                          switch (navItem.type) {
                            case 'link':
                              return (
                                <Menu.Item
                                  href={navItem.href}
                                  key={navItem.name}
                                  leadingContent={
                                    <NavMenuItemLeadingContent
                                      navItem={navItem}
                                    />
                                  }
                                  onClick={(ev) => {
                                    onLinkClick && onLinkClick(ev, navItem);
                                    handleShouldClose(ev, navItem, close);
                                  }}
                                  target={
                                    navItem.isExternal ? '_blank' : undefined
                                  }
                                  trailingContent={
                                    <NavMenuItemTrailingContent
                                      navItem={navItem}
                                    />
                                  }
                                >
                                  {navItem.name}
                                </Menu.Item>
                              );
                            case 'button':
                              return (
                                <Menu.Item
                                  key={navItem.name}
                                  leadingContent={
                                    <NavMenuItemLeadingContent
                                      navItem={navItem}
                                    />
                                  }
                                  onClick={(ev) => {
                                    onButtonClick && onButtonClick(ev, navItem);
                                    handleShouldClose(ev, navItem, close);
                                  }}
                                  trailingContent={
                                    <NavMenuItemTrailingContent
                                      navItem={navItem}
                                    />
                                  }
                                >
                                  {navItem.name}
                                </Menu.Item>
                              );
                            case 'label':
                              return (
                                <Menu.Item __type="label" key={navItem.name}>
                                  {navItem.name}
                                </Menu.Item>
                              );
                            case 'caption':
                              return (
                                <Menu.Item __type="caption" key={navItem.name}>
                                  {navItem.name}
                                </Menu.Item>
                              );
                            case 'separator':
                              return <Menu.Separator key={navItem.name} />;
                            default:
                              // This should not be reachable as types guard against it
                              // however, in cases where data sent in is dynamic, this
                              // protects the UI.
                              assertEdsUsage(
                                [typeof navItem === 'undefined'],
                                `Problem with navItem data: ${navItem}`,
                                'error',
                              );
                              return (
                                <Menu.Item key="error-unknown-nav-item-type">
                                  N/A
                                </Menu.Item>
                              );
                          }
                        })}
                      </Menu.Items>
                    </>
                  )}
                </Menu>
              )}
              {/* In horizontal layouts, we never render the horizontal rule */}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/**
 * Sub-component for the individual app header links.
 *
 * @param props Properties for the links within the App Header
 * @returns ReactNode
 */
const AppHeaderLink = forwardRef<HTMLAnchorElement, AppHeaderLinkProps>(
  (
    {
      children,
      className,
      icon,
      iconLayout = 'none',
      isCurrent = false,
      isExternal = false,
      isVertical,
      meta, // pulling out `meta` not to be spread on to other components
      name,
      type,
      ...other
    },
    ref,
  ) => {
    const componentClassName = clsx(
      styles['app-header__nav-item'],
      styles[`app-header__nav-item--link`],
      isCurrent && styles['app-header__nav-item--is-current'],
      isExternal && styles['app-header__nav-item--is-external'],
    );
    return (
      <a
        className={componentClassName}
        ref={ref}
        {...other}
        target={isExternal ? '_blank' : undefined}
      >
        <span
          className={clsx(
            iconLayout &&
              styles[`app-header__nav-item--icon-layout-${iconLayout}`],
          )}
        >
          {!(iconLayout === 'icon-only') && (
            <Text as="span" preset="appHeader-label">
              {children ?? name}
            </Text>
          )}
          {icon && iconLayout && (
            <Icon name={icon} purpose="decorative" size="24px" />
          )}
        </span>
        {isExternal && isVertical && (
          <Icon name="open-in-new" purpose="decorative" size="24px" />
        )}
      </a>
    );
  },
);

/**
 * Sub-component for the individual app header buttons.
 *
 * @param props Properties for the button within the App Header
 * @returns ReactNode
 */
const AppHeaderButton = forwardRef<HTMLButtonElement, AppHeaderButtonProps>(
  (
    {
      children,
      className,
      icon,
      iconLayout = 'none',
      isCurrent,
      isVertical,
      leadingContent,
      name,
      trailingContent,
      type,
      meta, // pulling out `meta` not to be spread on to other components
      user,
      ...other
    },
    ref,
  ) => {
    const componentClassName = clsx(
      className,
      styles['app-header__nav-item'],
      styles[`app-header__nav-item--button`],
      isCurrent && styles['app-header__nav-item--is-current'],
    );

    // TODO(next-major): remove handling of icon, or simplify to composable subcomponents
    return (
      <button className={componentClassName} ref={ref} {...other}>
        {isVertical && (
          <Icon name={'chevron-down'} purpose="decorative" size="24px" />
        )}
        <span
          className={clsx(
            styles['app-header__nav-item--button'],
            iconLayout &&
              styles[`app-header__nav-item--icon-layout-${iconLayout}`],
          )}
        >
          {!(iconLayout === 'icon-only') && (
            <Text as="span" preset="appHeader-label">
              {children ?? name}
            </Text>
          )}
          {icon && iconLayout && (
            <Icon name={icon} purpose="decorative" size="24px" />
          )}
          {!icon && leadingContent === 'avatar' && user && (
            <Avatar size="sm" user={user} />
          )}
        </span>
        {trailingContent && (
          <Icon name={trailingContent} purpose="decorative" size="24px" />
        )}
      </button>
    );
  },
);

/**
 * Sub-component for the AppHeader Drawer, which is reused for the vertical orientation of `AppHeader`
 *
 * @param props App drawer props for rendering the navigation group and wrapper
 * @returns ReactNote
 */
const AppHeaderDrawerContent = ({
  mode = 'default',
  navGroups,
  onButtonClick,
  onLinkClick,
}: AppHeaderDrawerProps) => (
  <div className={styles['drawer-content']}>
    {navGroups?.map((navGroup) => (
      <nav
        aria-label={navGroup.name}
        className={styles['drawer-content__nav-group']}
        key={navGroup.name}
      >
        <ul>
          {navGroup.navItems.map((navItem) => {
            return (
              <li
                className={clsx(
                  styles['drawer-content__nav-group-item'],
                  navItem.type &&
                    styles[
                      `drawer-content__nav-group-item--type-${navItem.type}`
                    ],
                )}
                key={navItem.name}
              >
                {navItem.type === 'button' && (
                  <AppHeaderButton
                    key={navItem.name}
                    {...navItem}
                    onClick={(ev) => {
                      mode === 'drawer' &&
                        document.getElementById('popover')?.hidePopover();
                      onButtonClick && onButtonClick(ev, navItem);
                    }}
                  />
                )}
                {navItem.type === 'link' && (
                  <AppHeaderLink
                    isVertical
                    key={navItem.name}
                    {...navItem}
                    onClick={(ev) => {
                      mode === 'drawer' &&
                        document.getElementById('popover')?.hidePopover();
                      onLinkClick && onLinkClick(ev, navItem);
                    }}
                  />
                )}
                {navItem.type === 'separator' && (
                  <Hr
                    className={styles['app-header__nav-item--separator']}
                    key={navItem.name}
                    {...navItem}
                  />
                )}
                {navItem.type === 'tree' && (
                  <menu>
                    <div className={styles['drawer-content__header-container']}>
                      <div>
                        <span
                          className={clsx(
                            styles['app-header__nav-item--button'],
                            styles['app-header_nav-item-label'],
                            navItem.iconLayout &&
                              styles[
                                `app-header__nav-item--icon-layout-${navItem.iconLayout}`
                              ],
                          )}
                        >
                          {!(navItem.iconLayout === 'icon-only') && (
                            <Text as="span" preset="overline-sm">
                              {navItem.name}
                            </Text>
                          )}
                          {navItem.icon && navItem.iconLayout && (
                            <Icon
                              name={navItem.icon}
                              purpose="decorative"
                              size="24px"
                            />
                          )}
                        </span>
                      </div>
                    </div>
                    <ul>
                      {navItem.navItems.map((navItem) => (
                        <li key={navItem.name}>
                          {navItem.type === 'button' && (
                            <AppHeaderButton
                              key={navItem.name}
                              {...navItem}
                              onClick={(ev) => {
                                mode === 'drawer' &&
                                  document
                                    .getElementById('popover')
                                    ?.hidePopover();
                                onButtonClick && onButtonClick(ev, navItem);
                              }}
                            />
                          )}
                          {navItem.type === 'link' && (
                            <AppHeaderLink
                              key={navItem.name}
                              {...navItem}
                              onClick={(ev) => {
                                mode === 'drawer' &&
                                  document
                                    .getElementById('popover')
                                    ?.hidePopover();
                                onLinkClick && onLinkClick(ev, navItem);
                              }}
                            />
                          )}
                          {navItem.type === 'separator' && (
                            <Hr
                              className={
                                styles['app-header__nav-item--separator']
                              }
                              key={navItem.name}
                              {...navItem}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  </menu>
                )}
                {navItem.type === 'menu' && (
                  <Menu>
                    {({ open, close }) => (
                      <>
                        <Menu.PlainButton as={React.Fragment}>
                          <AppHeaderButton
                            className={styles['app-header__menu-trigger']}
                            icon={navItem.icon}
                            iconLayout={
                              navItem.leadingContent === 'avatar'
                                ? 'left'
                                : navItem.iconLayout
                            }
                            isCurrent={open}
                            isVertical
                            leadingContent={navItem.leadingContent}
                            name={navItem.name}
                            type="button"
                            user={navItem.user}
                          >
                            {navItem.name}
                            {navItem.type === 'menu' && navItem.subLabel && (
                              <Text as="div" preset="appHeader-subLabel">
                                {navItem.subLabel}
                              </Text>
                            )}
                          </AppHeaderButton>
                        </Menu.PlainButton>
                        <HeadlessMenuItems
                          anchor={
                            mode === 'default'
                              ? { to: 'right end', gap: 24 }
                              : undefined
                          }
                          as={PopoverContainer}
                          className={clsx(
                            styles['app-header__nav-items'],
                            mode === 'drawer' &&
                              styles['app-header__nav-items--absolute'],
                          )}
                          modal={false}
                        >
                          {navItem.navItems?.map((navItem) => {
                            switch (navItem.type) {
                              case 'link':
                                return (
                                  <Menu.Item
                                    href={navItem.href}
                                    key={navItem.name}
                                    leadingContent={
                                      <NavMenuItemLeadingContent
                                        navItem={navItem}
                                      />
                                    }
                                    onClick={(ev) => {
                                      mode === 'drawer' &&
                                        document
                                          .getElementById('popover')
                                          ?.hidePopover();
                                      onLinkClick && onLinkClick(ev, navItem);
                                      handleShouldClose(ev, navItem, close);
                                    }}
                                    target={
                                      navItem.isExternal ? '_blank' : undefined
                                    }
                                    trailingContent={
                                      <NavMenuItemTrailingContent
                                        navItem={navItem}
                                      />
                                    }
                                  >
                                    {navItem.name}
                                  </Menu.Item>
                                );
                              case 'button': {
                                return (
                                  <Menu.Item
                                    key={navItem.name}
                                    leadingContent={
                                      <NavMenuItemLeadingContent
                                        navItem={navItem}
                                      />
                                    }
                                    onClick={(ev) => {
                                      mode === 'drawer' &&
                                        document
                                          .getElementById('popover')
                                          ?.hidePopover();
                                      onButtonClick &&
                                        onButtonClick(ev, navItem);
                                      handleShouldClose(ev, navItem, close);
                                    }}
                                    trailingContent={
                                      <NavMenuItemTrailingContent
                                        navItem={navItem}
                                      />
                                    }
                                  >
                                    {navItem.name}
                                  </Menu.Item>
                                );
                              }
                              case 'caption':
                                return (
                                  <Menu.Item
                                    __type="caption"
                                    key={navItem.name}
                                  >
                                    {navItem.name}
                                  </Menu.Item>
                                );
                              case 'label':
                                // TODO: change to Menu.Heading once Menu.Sections are used
                                return (
                                  <Menu.Item __type="label" key={navItem.name}>
                                    {navItem.name}
                                  </Menu.Item>
                                );
                              case 'separator':
                                return <Menu.Separator key={navItem.name} />;
                              default:
                                // This should not be reachable as types guard against it
                                // however, in cases where data sent in is dynamic, this
                                // protects the UI.
                                return (
                                  <Menu.Item key="error-unknown-nav-item-type">
                                    N/A
                                  </Menu.Item>
                                );
                            }
                          })}
                        </HeadlessMenuItems>
                      </>
                    )}
                  </Menu>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    ))}
  </div>
);

AppHeader.displayName = 'AppHeader';
